import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Megaphone,
  Star,
  Gift,
  MessageSquare,
  Plus,
  Trash2,
  Edit,
  ExternalLink,
  Send,
  TrendingUp,
  Users,
  BarChart3,
  Settings,
  Facebook,
  MapPin,
  Globe,
  Copy,
  Check,
} from "lucide-react";

const ICONES = [
  { value: "star", label: "Étoile", icon: Star },
  { value: "facebook", label: "Facebook", icon: Facebook },
  { value: "google", label: "Google Maps", icon: MapPin },
  { value: "globe", label: "Site web", icon: Globe },
];

const COULEURS = [
  { value: "#4F46E5", label: "Indigo" },
  { value: "#1877F2", label: "Facebook" },
  { value: "#4285F4", label: "Google" },
  { value: "#00B67A", label: "Trustpilot" },
  { value: "#E4405F", label: "Instagram" },
  { value: "#FF6B6B", label: "Rouge" },
  { value: "#10B981", label: "Vert" },
];

export default function Marketing() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [liensAvis, setLiensAvis] = useState([]);
  const [smsAuto, setSmsAuto] = useState([]);
  const [parrainages, setParrainages] = useState([]);
  const [avis, setAvis] = useState([]);
  const [smsEnvoyes, setSmsEnvoyes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [lienDialog, setLienDialog] = useState(false);
  const [smsDialog, setSmsDialog] = useState(false);
  const [editingLien, setEditingLien] = useState(null);
  const [editingSms, setEditingSms] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // {type: 'lien'|'sms', id, label}
  
  // Forms
  const [lienForm, setLienForm] = useState({
    nom: "",
    url: "",
    icone: "star",
    couleur: "#4F46E5",
    template_sms: "Bonjour {prenom} ! Merci pour votre achat. Donnez-nous votre avis : {lien}",
    actif: true,
  });
  
  const [smsForm, setSmsForm] = useState({
    nom: "",
    type_sms: "avis",
    delai_jours: 1,
    template: "",
    lien_avis_id: "",
    actif: true,
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, liensRes, smsAutoRes, parrainagesRes, avisRes, smsEnvoyesRes] = await Promise.all([
        axios.get(`${API}/marketing/stats`, { headers }),
        axios.get(`${API}/marketing/liens-avis`, { headers }),
        axios.get(`${API}/marketing/sms-auto`, { headers }),
        axios.get(`${API}/marketing/parrainages`, { headers }),
        axios.get(`${API}/marketing/avis`, { headers }),
        axios.get(`${API}/marketing/sms-envoyes`, { headers }),
      ]);
      setStats(statsRes.data);
      setLiensAvis(liensRes.data);
      setSmsAuto(smsAutoRes.data);
      setParrainages(parrainagesRes.data);
      setAvis(avisRes.data);
      setSmsEnvoyes(smsEnvoyesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Liens d'avis
  const handleSaveLien = async () => {
    try {
      if (editingLien) {
        await axios.put(`${API}/marketing/liens-avis/${editingLien.id}`, lienForm, { headers });
        toast.success("Lien modifié");
      } else {
        await axios.post(`${API}/marketing/liens-avis`, lienForm, { headers });
        toast.success("Lien ajouté");
      }
      setLienDialog(false);
      setEditingLien(null);
      setLienForm({ nom: "", url: "", icone: "star", couleur: "#4F46E5", template_sms: "Bonjour {prenom} ! Merci pour votre achat. Donnez-nous votre avis : {lien}", actif: true });
      fetchAll();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteLien = async (id) => {
    try {
      await axios.delete(`${API}/marketing/liens-avis/${id}`, { headers });
      toast.success("Lien supprimé");
      fetchAll();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleEditLien = (lien) => {
    setEditingLien(lien);
    setLienForm({ ...lien });
    setLienDialog(true);
  };

  // SMS Auto
  const handleSaveSms = async () => {
    try {
      if (editingSms) {
        await axios.put(`${API}/marketing/sms-auto/${editingSms.id}`, smsForm, { headers });
        toast.success("SMS modifié");
      } else {
        await axios.post(`${API}/marketing/sms-auto`, smsForm, { headers });
        toast.success("SMS ajouté");
      }
      setSmsDialog(false);
      setEditingSms(null);
      setSmsForm({ nom: "", type_sms: "avis", delai_jours: 1, template: "", lien_avis_id: "", actif: true });
      fetchAll();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteSms = async (id) => {
    try {
      await axios.delete(`${API}/marketing/sms-auto/${id}`, { headers });
      toast.success("SMS supprimé");
      fetchAll();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleToggleSmsActif = async (sms) => {
    try {
      await axios.put(`${API}/marketing/sms-auto/${sms.id}`, { actif: !sms.actif }, { headers });
      fetchAll();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // Copy code
  const [copiedCode, setCopiedCode] = useState(null);
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-indigo-600" />
            Marketing
          </h1>
          <p className="text-slate-500 mt-1">SMS automatiques, avis clients et parrainage</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto pb-1">
        {[
          { id: "dashboard", label: "Dashboard", icon: BarChart3 },
          { id: "liens", label: "Liens d'avis", icon: Star },
          { id: "sms", label: "SMS Auto", icon: MessageSquare },
          { id: "parrainage", label: "Parrainage", icon: Gift },
          { id: "avis", label: "Avis clients", icon: Star },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && stats && (
        <div className="space-y-6">
          {/* Reset SMS stats */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="reset-sms-stats-btn"
              onClick={async () => {
                const choice = window.prompt(
                  "Réinitialiser les SMS envoyés ?\n\n• Tape 'mois' pour effacer uniquement le mois en cours\n• Tape 'all' pour TOUT effacer (SMS historiques compris)\n• Tape autre chose pour annuler",
                  "mois"
                );
                if (choice !== "mois" && choice !== "all") return;
                try {
                  const res = await axios.delete(`${API}/marketing/sms-envoyes/reset?scope=${choice}`, { headers });
                  toast.success(`${res.data.deleted} SMS supprimé(s)`);
                  fetchAll();
                } catch (e) {
                  toast.error("Erreur lors de la réinitialisation");
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Réinitialiser stats SMS
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.sms_envoyes_mois}</p>
                  <p className="text-xs text-slate-500">SMS ce mois</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.note_moyenne || "-"}</p>
                  <p className="text-xs text-slate-500">Note moyenne</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.filleuls_total}</p>
                  <p className="text-xs text-slate-500">Filleuls total</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.gains_parrainages}€</p>
                  <p className="text-xs text-slate-500">Gains parrainages</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Parrains */}
          {stats.top_parrains && stats.top_parrains.length > 0 && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-indigo-600" />
                Top Parrains
              </h3>
              <div className="space-y-3">
                {stats.top_parrains.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? "bg-yellow-400 text-yellow-900" :
                        i === 1 ? "bg-slate-300 text-slate-700" :
                        i === 2 ? "bg-orange-300 text-orange-800" :
                        "bg-slate-200 text-slate-600"
                      }`}>
                        {i + 1}
                      </span>
                      <span className="font-medium">{p.nom}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-slate-500">{p.filleuls} filleul(s)</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {p.gains}€
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Avis récents */}
          {avis.length > 0 && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Derniers avis
              </h3>
              <div className="space-y-3">
                {avis.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-4 h-4 ${n <= a.note ? "text-yellow-400 fill-yellow-400" : "text-slate-300"}`}
                        />
                      ))}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{a.client_nom}</p>
                      {a.commentaire && <p className="text-sm text-slate-600 mt-1">{a.commentaire}</p>}
                    </div>
                    <Badge variant="outline" className="text-xs">{a.plateforme}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Liens d'avis Tab */}
      {activeTab === "liens" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">
              Configurez les liens vers vos pages d'avis (Facebook, Google, etc.)
            </p>
            <Dialog open={lienDialog} onOpenChange={(open) => {
              setLienDialog(open);
              if (!open) {
                setEditingLien(null);
                setLienForm({ nom: "", url: "", icone: "star", couleur: "#4F46E5", template_sms: "Bonjour {prenom} ! Merci pour votre achat. Donnez-nous votre avis : {lien}", actif: true });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un lien
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingLien ? "Modifier le lien" : "Nouveau lien d'avis"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Nom de la plateforme</Label>
                    <Input
                      placeholder="Ex: Facebook, Google Maps..."
                      value={lienForm.nom}
                      onChange={(e) => setLienForm({ ...lienForm, nom: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>URL du lien</Label>
                    <Input
                      placeholder="https://..."
                      value={lienForm.url}
                      onChange={(e) => setLienForm({ ...lienForm, url: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Icône</Label>
                      <Select value={lienForm.icone} onValueChange={(v) => setLienForm({ ...lienForm, icone: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ICONES.map((i) => (
                            <SelectItem key={i.value} value={i.value}>
                              <span className="flex items-center gap-2">
                                <i.icon className="w-4 h-4" />
                                {i.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Couleur</Label>
                      <Select value={lienForm.couleur} onValueChange={(v) => setLienForm({ ...lienForm, couleur: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COULEURS.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              <span className="flex items-center gap-2">
                                <span className="w-4 h-4 rounded" style={{ backgroundColor: c.value }}></span>
                                {c.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Template SMS</Label>
                    <Textarea
                      placeholder="Utilisez {prenom} et {lien}"
                      value={lienForm.template_sms}
                      onChange={(e) => setLienForm({ ...lienForm, template_sms: e.target.value })}
                      rows={3}
                    />
                    <p className="text-xs text-slate-500 mt-1">Variables: {"{prenom}"}, {"{lien}"}</p>
                  </div>
                  <Button onClick={handleSaveLien} className="w-full bg-indigo-600 hover:bg-indigo-700">
                    {editingLien ? "Modifier" : "Ajouter"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {liensAvis.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Star className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucun lien configuré</p>
              <p className="text-sm text-slate-400 mt-1">Ajoutez vos liens Facebook, Google Maps, etc.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {liensAvis.map((lien) => {
                const IconComponent = ICONES.find(i => i.value === lien.icone)?.icon || Star;
                return (
                  <div key={lien.id} className="bg-white rounded-xl border p-4 flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: lien.couleur + "20" }}
                    >
                      <IconComponent className="w-6 h-6" style={{ color: lien.couleur }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{lien.nom}</h3>
                        {!lien.actif && <Badge variant="outline" className="text-xs">Inactif</Badge>}
                      </div>
                      <a href={lien.url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1">
                        {lien.url.substring(0, 40)}...
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEditLien(lien)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" data-testid="delete-lien-btn" onClick={() => setConfirmDelete({ type: 'lien', id: lien.id, label: lien.nom || 'ce lien' })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* SMS Auto Tab */}
      {activeTab === "sms" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">
              SMS envoyés automatiquement après chaque livraison
            </p>
            <Dialog open={smsDialog} onOpenChange={(open) => {
              setSmsDialog(open);
              if (!open) {
                setEditingSms(null);
                setSmsForm({ nom: "", type_sms: "avis", delai_jours: 1, template: "", lien_avis_id: "", actif: true });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un SMS
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingSms ? "Modifier le SMS" : "Nouveau SMS automatique"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Nom</Label>
                    <Input
                      placeholder="Ex: Demande d'avis J+1"
                      value={smsForm.nom}
                      onChange={(e) => setSmsForm({ ...smsForm, nom: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Type</Label>
                      <Select value={smsForm.type_sms} onValueChange={(v) => setSmsForm({ ...smsForm, type_sms: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="avis">Demande d'avis</SelectItem>
                          <SelectItem value="parrainage">Parrainage</SelectItem>
                          <SelectItem value="promo">Promo / Relance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Délai (jours)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={smsForm.delai_jours}
                        onChange={(e) => setSmsForm({ ...smsForm, delai_jours: parseInt(e.target.value) || 1 })}
                      />
                    </div>
                  </div>
                  {smsForm.type_sms === "avis" && liensAvis.length > 0 && (
                    <div>
                      <Label>Lien d'avis à utiliser</Label>
                      <Select value={smsForm.lien_avis_id} onValueChange={(v) => setSmsForm({ ...smsForm, lien_avis_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir un lien" />
                        </SelectTrigger>
                        <SelectContent>
                          {liensAvis.map((l) => (
                            <SelectItem key={l.id} value={l.id}>{l.nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Template du SMS</Label>
                    <Textarea
                      placeholder="Utilisez {prenom}, {lien}, {code_parrain}"
                      value={smsForm.template}
                      onChange={(e) => setSmsForm({ ...smsForm, template: e.target.value })}
                      rows={4}
                    />
                    <p className="text-xs text-slate-500 mt-1">Variables: {"{prenom}"}, {"{lien}"}, {"{code_parrain}"}</p>
                  </div>
                  <Button onClick={handleSaveSms} className="w-full bg-indigo-600 hover:bg-indigo-700">
                    {editingSms ? "Modifier" : "Ajouter"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {smsAuto.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucun SMS automatique configuré</p>
            </div>
          ) : (
            <div className="space-y-3">
              {smsAuto.map((sms) => (
                <div key={sms.id} className={`bg-white rounded-xl border p-4 ${!sms.actif ? "opacity-50" : ""}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Badge variant={sms.type_sms === "avis" ? "default" : sms.type_sms === "parrainage" ? "secondary" : "outline"}>
                        {sms.type_sms === "avis" ? "Avis" : sms.type_sms === "parrainage" ? "Parrainage" : "Promo"}
                      </Badge>
                      <h3 className="font-medium">{sms.nom}</h3>
                      <span className="text-sm text-slate-500">J+{sms.delai_jours}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={sms.actif} onCheckedChange={() => handleToggleSmsActif(sms)} />
                      <Button variant="ghost" size="icon" className="text-red-500" data-testid="delete-sms-btn" onClick={() => setConfirmDelete({ type: 'sms', id: sms.id, label: sms.nom || 'ce SMS auto' })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{sms.template}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Parrainage Tab */}
      {activeTab === "parrainage" && (
        <div className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Comment ça marche ?
            </h3>
            <ol className="mt-2 text-sm text-indigo-700 space-y-1 list-decimal list-inside">
              <li>Après une livraison, le client reçoit un code unique (ex: MARIE15)</li>
              <li>Il partage ce code avec ses amis</li>
              <li>Quand un ami commande avec ce code, notez-le ici</li>
              <li>Le parrain cumule des gains (15€ par filleul par défaut)</li>
            </ol>
          </div>

          {parrainages.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucun parrainage actif</p>
              <p className="text-sm text-slate-400 mt-1">Les codes sont créés automatiquement lors de l'envoi du SMS parrainage</p>
            </div>
          ) : (
            <div className="space-y-3">
              {parrainages.map((p) => (
                <div key={p.id} className="bg-white rounded-xl border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{p.client_nom}</h3>
                      <p className="text-sm text-slate-500">{p.client_telephone}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-slate-200"
                        onClick={() => copyCode(p.code)}
                      >
                        <span className="font-mono font-bold text-indigo-600">{p.code}</span>
                        {copiedCode === p.code ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {p.total_gains}€
                      </Badge>
                    </div>
                  </div>
                  {p.filleuls && p.filleuls.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-slate-500 mb-2">Filleuls ({p.filleuls.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {p.filleuls.map((f, i) => (
                          <Badge key={i} variant="outline">{f.client_nom}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Avis Tab */}
      {activeTab === "avis" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">
              {avis.length} avis reçus • Note moyenne: {stats?.note_moyenne || "-"}/5
            </p>
          </div>

          {avis.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Star className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucun avis reçu</p>
            </div>
          ) : (
            <div className="space-y-3">
              {avis.map((a) => (
                <div key={a.id} className="bg-white rounded-xl border p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                              key={n}
                              className={`w-5 h-5 ${n <= a.note ? "text-yellow-400 fill-yellow-400" : "text-slate-300"}`}
                            />
                          ))}
                        </div>
                        <Badge variant="outline" className="text-xs">{a.plateforme}</Badge>
                      </div>
                      <p className="font-medium">{a.client_nom}</p>
                      {a.commentaire && (
                        <p className="text-slate-600 mt-2">{a.commentaire}</p>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(a.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirm delete dialog (remplace confirm() natif qui peut être bloqué) */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Voulez-vous vraiment supprimer <strong>{confirmDelete?.label}</strong> ? Cette action est définitive.
          </p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-btn">
              Annuler
            </Button>
            <Button
              variant="destructive"
              data-testid="confirm-delete-btn"
              onClick={async () => {
                if (!confirmDelete) return;
                if (confirmDelete.type === 'lien') await handleDeleteLien(confirmDelete.id);
                else if (confirmDelete.type === 'sms') await handleDeleteSms(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
